// src/utils/lazyWithRetry.ts
// React.lazy wrapper that survives a deploy mid-session. When a chunk's hash
// changes under a running tab, the old chunk URL 404s (and the SPA fallback
// returns index.html, hence "Unexpected token '<'"). Instead of leaving the
// player on an error screen, reload once to pick up the new build.
import React from "react";

const RELOAD_FLAG = "moonrock-chunk-reload";

export function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  if (!e) return false;
  const msg = String(e.message || "");
  return (
    e.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Unexpected token '<'/.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

/** Reload once per session to recover from a stale deploy. Returns true if it reloaded. */
export function reloadOnceForChunkError(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* storage blocked: still reload once via in-memory flag */
    if ((window as any).__moonrockChunkReloaded) return false;
    (window as any).__moonrockChunkReloaded = true;
  }
  window.location.reload();
  return true;
}

/** Clear the guard after a successful load so a later deploy can recover again. */
export function clearChunkReloadFlag(): void {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    // Keep the flag for a minute so a hard loop can't form, then release it.
    if (at && Date.now() - at > 60_000) sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      const mod = await factory();
      clearChunkReloadFlag();
      return mod;
    } catch (err) {
      if (isChunkLoadError(err)) {
        // One retry (the CDN may just have been mid-deploy), then reload.
        try {
          const mod = await factory();
          clearChunkReloadFlag();
          return mod;
        } catch (err2) {
          if (isChunkLoadError(err2) && reloadOnceForChunkError()) {
            // Keep React suspended while the page reloads.
            return new Promise<never>(() => {});
          }
          throw err2;
        }
      }
      throw err;
    }
  });
}
